import type { LinkProps } from '@tanstack/react-router'
import { useContext, type ReactNode } from 'react'

import BackButton from '@/components/ui/BackButton'
import { SectionStripContext } from '@/components/ui/section-strip-context'
import { cn } from '@/lib/utils'

interface TitleRowProps {
  /** The page's own name. Rendered as the page's one `<h1>`. */
  title: string
  /**
   * A parent route. Present → a bare 32px back chevron leads the row (Account
   * sub-pages, Notifications, Basket/Broker/IPO/Newsletter detail). Absent →
   * a plain title (Home, the Market family's own pages).
   */
  back?: NonNullable<LinkProps['to']>
  /** The back link's search, when the parent has more than one view. */
  backSearch?: LinkProps['search']
  /**
   * A picture between the back button and the title, centred on the title block — a basket's
   * round cover. With one, the eyebrow moves into the title block beside it, as the mock's basket
   * head draws it.
   */
  leading?: ReactNode
  /** A sentence-case line above the title — a lecture's course, a basket's kind. */
  eyebrow?: ReactNode
  /** On the title's own baseline, small — "Updated 3:15 PM", "NEPSE · 7 Aswin 2083". */
  meta?: ReactNode
  /** One line under the title — Public Issues, Portfolio health, Newsletters. */
  subtitle?: ReactNode
  /**
   * The day in one sentence under the title, at the section rung — Market
   * Overview's "NEPSE fell 0.73% today." (`4236:100580`). Heavier than `subtitle`,
   * which describes the page rather than the day.
   */
  headline?: ReactNode
  /** Right-hand slot: a "+ New alert" button, a Share icon. */
  actions?: ReactNode
  /**
   * Below `sm` the actions drop under the title, wrapping, instead of squeezing it — for a slot
   * too wide to share a phone's row (the chat page's level, History, New chat and More).
   */
  wrapActions?: boolean
  className?: string
}

/**
 * The page title-row every family shares: title, an optional back button,
 * optional inline meta, subtitle, headline, eyebrow and a right-hand slot for
 * actions.
 *
 * Replaces the per-family pill headers (MarketFamilyHeader, BrokerFamilyHeader,
 * AccountPageHeader, ScreenerHeader, bare `<h1>`s…) — those own their own
 * screens and are for their implementers to retire in favour of this, not for
 * this component to reach into.
 *
 * The title is `text-h-2xl`, 32px on a 36px line (Figma 34 → 36, 4-grid). The
 * back button is centred on that first line, 8px before the title (the mock's Notifications head),
 * however many lines the title wraps to.
 *
 * Under ScreenLayout's section tab strip (`SectionStripContext`), from `lg`
 * up, the strip names the page: the `h1` stays, for screen readers only
 * (`sr-only`, never `display: none` — the page keeps its heading), and a
 * row with nothing else in it takes no room at all. Everything else in the
 * row still shows. Below `lg` there is no strip and nothing changes.
 */
export default function TitleRow({
  title,
  back,
  backSearch,
  leading,
  eyebrow,
  meta,
  subtitle,
  headline,
  actions,
  wrapActions = false,
  className,
}: TitleRowProps) {
  const underStrip = useContext(SectionStripContext)
  const titleOnly = back === undefined && !eyebrow && !meta && !subtitle && !headline && !actions

  const eyebrowLine = eyebrow ? (
    <p className="text-h-xs text-muted-foreground-subtle">{eyebrow}</p>
  ) : null

  return (
    <div className={cn('flex flex-col gap-1', underStrip && titleOnly && 'lg:sr-only', className)}>
      {leading ? null : eyebrowLine}
      <div
        className={cn(
          'flex',
          leading ? 'items-center' : 'items-start',
          back !== undefined ? 'gap-2' : 'gap-3',
          wrapActions && 'max-sm:flex-wrap',
        )}
      >
        {back !== undefined ? (
          <div className="flex h-9 shrink-0 items-center">
            <BackButton to={back} {...(backSearch === undefined ? {} : { search: backSearch })} />
          </div>
        ) : null}
        {leading ? <div className="mr-2 shrink-0">{leading}</div> : null}
        <div
          className={cn('flex min-w-0 flex-1 flex-col gap-1', wrapActions && 'max-sm:basis-full')}
        >
          {leading ? eyebrowLine : null}
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h1 className={cn('text-h-2xl text-foreground', underStrip && 'lg:sr-only')}>
              {title}
            </h1>
            {meta ? (
              <span className="text-body-xs text-muted-foreground-subtle">{meta}</span>
            ) : null}
          </div>
          {subtitle ? <p className="text-body-m text-muted-foreground">{subtitle}</p> : null}
          {headline ? (
            <p className={cn('mt-5 text-h-section text-foreground', underStrip && 'lg:mt-0')}>
              {headline}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div
            className={cn(
              'flex shrink-0 items-center gap-2',
              wrapActions && 'max-sm:max-w-full max-sm:min-w-0',
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  )
}
